/**
 * Client Error Log Viewer Component
 *
 * Displays client-side errors captured by the error logger service.
 * Can be embedded in the dashboard or used as a standalone viewer.
 */

import { useState, useEffect } from 'react';
import {
  AlertCircle,
  XCircle,
  AlertTriangle,
  Info,
  X,
  Download,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import {
  errorLogger,
  ErrorLogEntry,
  ErrorSeverity,
  ErrorCategory,
  ErrorStats,
} from '../../services/platform-admin/error-logger.service';

interface ClientErrorLogProps {
  maxHeight?: string;
  showStats?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export default function ClientErrorLog({
  maxHeight = '600px',
  showStats = true,
  autoRefresh = false,
  refreshInterval = 5000,
}: ClientErrorLogProps) {
  const [errors, setErrors] = useState<ErrorLogEntry[]>([]);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [selectedError, setSelectedError] = useState<ErrorLogEntry | null>(null);
  const [severityFilter, setSeverityFilter] = useState<ErrorSeverity | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<ErrorCategory | ''>('');

  const loadErrors = () => {
    const filters: any = { limit: 100 };
    if (severityFilter) filters.severity = severityFilter;
    if (categoryFilter) filters.category = categoryFilter;

    const errorList = errorLogger.getErrors(filters);
    setErrors(errorList);
    setStats(errorLogger.getStats());
  };

  useEffect(() => {
    loadErrors();

    // Subscribe to new errors
    const unsubscribe = errorLogger.subscribe(() => {
      loadErrors();
    });

    return unsubscribe;
  }, [severityFilter, categoryFilter]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadErrors, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const handleExport = () => {
    const json = errorLogger.exportErrors({ limit: 1000 });
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-errors-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all client-side error logs?')) {
      errorLogger.clearErrors();
      loadErrors();
    }
  };

  const handleClearOld = () => {
    if (window.confirm('Clear errors older than 24 hours?')) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      errorLogger.clearOldErrors(yesterday);
      loadErrors();
    }
  };

  const getSeverityIcon = (severity: ErrorSeverity) => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return <XCircle className="h-5 w-5 text-red-600" />;
      case ErrorSeverity.HIGH:
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      case ErrorSeverity.MEDIUM:
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case ErrorSeverity.LOW:
        return <Info className="h-5 w-5 text-blue-600" />;
    }
  };

  const getSeverityBadge = (severity: ErrorSeverity) => {
    const colors = {
      [ErrorSeverity.CRITICAL]: 'bg-red-100 text-red-800',
      [ErrorSeverity.HIGH]: 'bg-orange-100 text-orange-800',
      [ErrorSeverity.MEDIUM]: 'bg-yellow-100 text-yellow-800',
      [ErrorSeverity.LOW]: 'bg-blue-100 text-blue-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[severity]}`}>
        {severity}
      </span>
    );
  };

  const getCategoryBadge = (category: ErrorCategory) => {
    const colors = {
      [ErrorCategory.AUTHENTICATION]: 'bg-red-50 text-red-700 border-red-200',
      [ErrorCategory.AUTHORIZATION]: 'bg-orange-50 text-orange-700 border-orange-200',
      [ErrorCategory.NETWORK]: 'bg-purple-50 text-purple-700 border-purple-200',
      [ErrorCategory.API]: 'bg-blue-50 text-blue-700 border-blue-200',
      [ErrorCategory.VALIDATION]: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      [ErrorCategory.DATABASE]: 'bg-green-50 text-green-700 border-green-200',
      [ErrorCategory.UNKNOWN]: 'bg-gray-50 text-gray-700 border-gray-200',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${colors[category]}`}>
        {category}
      </span>
    );
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {showStats && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-600">Total Errors</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-600">Last Hour</div>
            <div className="text-xl font-bold text-orange-600 mt-1">{stats.lastHour}</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-600">Last 24h</div>
            <div className="text-xl font-bold text-blue-600 mt-1">{stats.last24Hours}</div>
          </div>
          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-600">Critical</div>
            <div className="text-xl font-bold text-red-600 mt-1">
              {stats.bySeverity[ErrorSeverity.CRITICAL]}
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as ErrorSeverity | '')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">All Severities</option>
            <option value={ErrorSeverity.CRITICAL}>Critical</option>
            <option value={ErrorSeverity.HIGH}>High</option>
            <option value={ErrorSeverity.MEDIUM}>Medium</option>
            <option value={ErrorSeverity.LOW}>Low</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as ErrorCategory | '')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            <option value={ErrorCategory.AUTHENTICATION}>Authentication</option>
            <option value={ErrorCategory.AUTHORIZATION}>Authorization</option>
            <option value={ErrorCategory.NETWORK}>Network</option>
            <option value={ErrorCategory.API}>API</option>
            <option value={ErrorCategory.VALIDATION}>Validation</option>
            <option value={ErrorCategory.DATABASE}>Database</option>
            <option value={ErrorCategory.UNKNOWN}>Unknown</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadErrors}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={handleExport}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Export to JSON"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={handleClearOld}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear Old
          </button>
          <button
            onClick={handleClearAll}
            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Clear All"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Error List */}
      <div
        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
        style={{ maxHeight }}
      >
        <div className="overflow-auto h-full">
          {errors.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Info className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No client-side errors logged</p>
              <p className="text-sm mt-1">Errors will appear here when they occur</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {errors.map((error) => (
                <div
                  key={error.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedError(error)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">{getSeverityIcon(error.severity)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getSeverityBadge(error.severity)}
                        {getCategoryBadge(error.category)}
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(error.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1">{error.message}</p>
                      {error.endpoint && (
                        <p className="text-xs text-gray-600 font-mono">{error.endpoint}</p>
                      )}
                      {error.httpStatus && (
                        <span className="inline-block text-xs text-gray-500 mt-1">
                          HTTP {error.httpStatus}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error Detail Modal */}
      {selectedError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Error Details</h3>
              <button
                onClick={() => setSelectedError(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Timestamp</label>
                  <p className="text-sm text-gray-900">
                    {selectedError.timestamp.toLocaleString()}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Severity</label>
                  <div className="mt-1">{getSeverityBadge(selectedError.severity)}</div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Category</label>
                  <div className="mt-1">{getCategoryBadge(selectedError.category)}</div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Message</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedError.message}</p>
                </div>

                {selectedError.endpoint && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Endpoint</label>
                    <p className="text-sm text-gray-900 font-mono mt-1">
                      {selectedError.endpoint}
                    </p>
                  </div>
                )}

                {selectedError.httpStatus && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">HTTP Status</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedError.httpStatus}</p>
                  </div>
                )}

                {selectedError.details && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Details</label>
                    <pre className="text-xs text-gray-900 bg-gray-50 p-3 rounded-lg mt-1 overflow-auto">
                      {JSON.stringify(selectedError.details, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedError.stack && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Stack Trace</label>
                    <pre className="text-xs text-gray-900 bg-gray-50 p-3 rounded-lg mt-1 overflow-auto">
                      {selectedError.stack}
                    </pre>
                  </div>
                )}

                {selectedError.context && Object.keys(selectedError.context).length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Context</label>
                    <pre className="text-xs text-gray-900 bg-gray-50 p-3 rounded-lg mt-1 overflow-auto">
                      {JSON.stringify(selectedError.context, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
